<script lang="ts">
  import { onMount } from "svelte";
  import active from "svelte-spa-router/active";
  import { location } from "svelte-spa-router";
  import ListNavigationItem from "./ListNavigationItem.svelte";
  import { MyPagesStartModel } from "$shared/models/mod.ts";

  const NAVIGATE_BACK = "navigateBack";

  export let data: MyPagesStartModel;

  const misc = {
    //messages: { label: "Meddelanden" },
    //settings: { label: "Inställningar" },
    contact: { label: "Kontakta Parkando" },
    about: { label: "Om Mina sidor" },
  };

  let currentView = "";
  let currentId = "";
  let isLoadedFromApp = false;

  $: {
    if (import.meta.env.DEV) console.log("location changed:", $location);
    currentView = $location.split("/")[1];
    currentId = $location.split("/")[2];
  }

  /**
   * Navigate back in the app
   */
  const appNavigateBack = () => {
    (window as any).ReactNativeWebView.postMessage(NAVIGATE_BACK);
  };

  const getSiteLinks = () => {
    const siteArray = data.sites.map((site) => ({
      id: site.id,
      title: site.name,
      link: `#/site/${site.id}`,
    }));

    if (data.spotShares.length === 0) {
      return siteArray;
    } else {
      console.log(data);
      const existingSiteIds = new Set(siteArray.map((site) => site.id));

      const spotShareArray = data.spotShares
        .filter((spotShare) => !existingSiteIds.has(spotShare.siteId))
        .map((spotShare) => ({
          id: spotShare.siteId,
          title: spotShare.siteName,
          link: `#/site/${spotShare.siteId}`,
        }));

      return siteArray.concat(spotShareArray);
    }
  };

  onMount(async () => {
    isLoadedFromApp =
      new URLSearchParams(window.location.search).get("app") === "true";
  });
</script>

<!-- Side menu -->
<div
  class:max-mobile:hidden={currentView}
  class="w-[285px] max-mobile:w-full h-auto flex-2 px-[24px] py-[24px] border-r-[#C5DEDB] border-r-[0.5px] max-mobile:px-[8px] max-mobile:py-[8px]"
>
  <!-- Special styling for the app -->
  {#if isLoadedFromApp}
    <div class="px-[24px] py-[24px] max-mobile:px-[0px] max-mobile:py-[0px]">
      <h2 class="text-[#181818] text-[17px] -mt-[30px] inline relative">
        Mina sidor
      </h2>
    </div>

    <!-- Spots and services -->
    {#if data.sites.length > 0}
      <h3 class="text-[14px] text-[#181818] pt-[24px] pb-[16px]">
        Parkering och tjänster
      </h3>
      {#each getSiteLinks() as { id, link, title }, index}
        {#if data.siteHasActiveAgreements(id)}
          <ListNavigationItem
            {title}
            isFirst={index === 0}
            isLast={index === getSiteLinks().length - 1}
            {link}
          />
        {/if}
      {/each}
    {/if}

    <!-- Payment profiles and invoicing -->
    {#if data.debtors.length > 0}
      <h3 class="text-[14px] text-[#181818] pt-[24px] pb-[16px]">
        Betalprofiler och faktura
      </h3>
      {#each data.debtors as debtor, index}
        <ListNavigationItem
          title={`${debtor.debtor.data.name} - ${debtor.debtor.data.contactEmail}`}
          isFirst={index === 0}
          isLast={index === data.debtors.length - 1}
          link={`#/profile/${debtor.debtor.data.id}`}
        />
      {/each}
    {/if}

    <!-- Permit agreements -->
    {@const permits = data.getPermitAgreements()}
    {#if permits.length > 0}
      <h3 class="text-[14px] text-[#181818] pt-[24px] pb-[16px]">Tillstånd</h3>
      {#each permits as agreement, index}
        <ListNavigationItem
          title={`${agreement.number} ${
            data.sites.find((site) => site.id === agreement.data.siteId)
              ?.name
          }`}
          isFirst={index === 0}
          isLast={index === permits.length - 1}
          link={`#/agreements/${agreement.id}`}
        />
      {/each}
    {/if}

    <!-- Reserved agreements -->
    {@const reserved = data.getReservedAgreements()}
    {#if reserved.length > 0}
      <h3 class="text-[14px] text-[#181818] pt-[24px] pb-[16px]">
        Reserverad parkering
      </h3>
      {#each reserved as agreement, index}
        <ListNavigationItem
          title={`${agreement.number} ${
            data.sites.find((site) => site.id === agreement.data.siteId)
              ?.name
          }`}
          isFirst={index === 0}
          isLast={index === reserved.length - 1}
          link={`#/agreements/${agreement.id}`}
        />
      {/each}
    {/if}

    <!-- Applicants -->
    {#if data.applicants.length > 0}
      <h3 class="text-[14px] text-[#181818] pt-[24px] pb-[16px]">
        Intresselistor
      </h3>
      {#each data.applicants as applicant, index}
        <ListNavigationItem
          title={`${applicant.queueName} (${
            applicant.debtor?.email?.substring(
              0,
              8,
            )
          }...)`}
          isFirst={index === 0}
          isLast={index === data.agreements.length - 1}
          link={`#/applicant/${applicant.id}`}
        />
      {/each}
    {/if}

    <!-- Misc pages -->
    <div class="pt-[24px]">
      {#each Object.entries(misc) as [path, { label }], index}
        <ListNavigationItem
          title={label}
          isFirst={index === 0}
          isLast={index === Object.entries(misc).length - 1}
          link={`#/${path}`}
        />
      {/each}
    </div>
  {:else}
    <!-- Ordinary web app styling -->
    <h1 class="text-[19px] text-[#181818] font-bold pb-[16px]">Mina sidor</h1>

    <!-- Spots and services -->
    {#if data.sites.length > 0}
      <h3 class="text-[13px] font-bold text-[#58686A] mb-[8px]">
        Parkering och tjänster
      </h3>
      {#each getSiteLinks() as { link, title, id }}
        {#if data.siteHasActiveAgreements(id)}
          <a
            use:active={{ className: "selected" }}
            class="link"
            class:selected={currentId === id}
            href={link}
          >{title}</a>
        {/if}
      {/each}
    {/if}

    <!-- Payment profiles and invoicing -->
    {#if data.debtors.length > 0}
      <h3 class="text-[13px] font-bold text-[#58686A] mb-[8px] mt-[16px]">
        Betalprofiler och faktura
      </h3>
      {#each data.debtors as debtor}
        <a
          use:active={{ className: "selected" }}
          class="link"
          class:selected={currentId === debtor.debtor.data.id}
          href={`#/profile/${debtor.debtor.data.id}`}
        >{`${debtor.debtor.data.name} - ${debtor.debtor.data.contactEmail}`}</a>
      {/each}
    {/if}

    <!-- Permit agreements -->
    {@const permits = data.getPermitAgreements()}
    {#if permits.length > 0}
      <h3 class="text-[13px] font-bold text-[#58686A] mb-[8px] mt-[16px]">
        Tillstånd
      </h3>
      {#each permits as agreement}
        <a
          use:active={{ className: "selected" }}
          class="link"
          class:selected={currentId === agreement.id}
          href={`#/agreements/${agreement.id}`}
        >
          <span class={agreement.getStateColor()}>
            ●
          </span>
          {agreement.number}
          {data.findSite(agreement.data.siteId)?.name}
        </a>
      {/each}
    {/if}

    <!-- Reserved agreements -->
    {@const reserved = data.getReservedAgreements()}
    {#if reserved.length > 0}
      <h3 class="text-[13px] font-bold text-[#58686A] mb-[8px] mt-[16px]">
        Avtal
      </h3>
      {#each reserved as agreement}
        <a
          use:active={{ className: "selected" }}
          class="link"
          class:selected={currentId === agreement.id}
          href={`#/agreements/${agreement.id}`}
        >
          <span class={agreement.getStateColor()}>
            ●
          </span>
          {agreement.number}
          {data.findSite(agreement.data.siteId)?.name}
        </a>
      {/each}
    {/if}

    <!-- Applicants -->
    {#if data.applicants.length > 0}
      <h3 class="text-[13px] font-bold text-[#58686A] mb-[8px] mt-[16px]">
        Intresselistor
      </h3>
      {#each data.applicants as applicant}
        <a
          use:active={{ className: "selected" }}
          class="link"
          class:selected={currentId === applicant.id}
          href={`#/applicant/${applicant.id}`}
        >
          {applicant.queueName} ({
            applicant.debtor?.email?.substring(
              0,
              8,
            )
          }...)</a>
      {/each}
    {/if}

    <!-- Misc pages -->
    <div class="pt-[24px]">
      {#each Object.entries(misc) as [path, { label }]}
        <a
          use:active={{ className: "selected" }}
          class="link"
          class:selected={currentView === path}
          href={`#/${path}`}
        >{label}</a>
      {/each}
    </div>
  {/if}
</div>

<style lang="postcss">
  .link {
    @apply text-[15px] font-medium text-[#58686a] pt-[4px] pb-[4px] block;
  }
  .link:hover {
    @apply bg-[#cadfe0] px-[19px] mx-[-19px] rounded;
  }
  .selected {
    @apply bg-[#cadfe0] px-[19px] mx-[-19px] font-semibold rounded;
  }
</style>
